// 域名相关处理工具库

// 需要与旧官网server/helpers/getDomains.js保持大致一致。注：getDomainInfos不一样。
import _ from 'lodash';

import logger from './logger';
import { MultiReplacer, relationMapCombination } from './utils/stringReplaceUtils';

// 后续挪到配置文件里
const commonConfig = {
  domainFormatConfig: {
    keepUnnecessaryDomainInUrl: true, // 同域名下是否保留域名，true为保留，false为去掉域名只保留相对域名
    defaultExtendReplaceOn: true, // 是否默认开启额外转换，转换的map在下面。用于文档等地方包含中国站域名漏改为国际站的情况
    extendReplaceMap: {
      'console.cloud.tencent.com': 'console.tencentcloud.com',
      'buy.cloud.tencent.com': 'buy.tencentcloud.com',
    },
    // 同等域名列表，其中每行第一个为原始域名，后面的为衍生域名
    sameDomainList: [
      ['www.tencentcloud.com', 'intl.cloud.tencent.com'],
      ['console.tencentcloud.com', 'console.intl.cloud.tencent.com'],
      ['buy.tencentcloud.com', 'buy.intl.cloud.tencent.com'],
    ],
  },
};

/**
 * @typedef {Object} DomainInfosType
 * @property {String} QCMAIN_HOST 官网域名
 * @property {String} QCCONSOLE_HOST 控制台域名
 * @property {String} QCLOUD_ROOT_HOST cookie域名
 * @property {String} QCBUY_HOST 购买页域名
 * @property {String} QCCONSOLE_CN_HOST 中国站控制台域名
 * @property {String} QCBUY_CN_HOST 中国站购买页域名
 * @property {String} CHINA_HOST 中国站官网域名
 * @author yuiffydong
 */

const BASE_DOMAINS = {
  QCMAIN_HOST: 'www.tencentcloud.com',
  QCLOUD_ROOT_HOST: 'cloud.tencent.com',
  QCCONSOLE_HOST: 'console.tencentcloud.com',
  QCCONSOLE_CN_HOST: 'console.cloud.tencent.com',
  QCBUY_HOST: 'buy.tencentcloud.com',
  QCBUY_CN_HOST: 'buy.cloud.tencent.com',
  CHINA_HOST: 'cloud.tencent.com', // 中国站域名。在国际站域名修改后会和国际站域名、cookie域名区分开，单独处理
};

/**
 * 生成需要的各种相对域名（控制台、购买页等）
 * @param  {String} host 基础host
 * @return {DomainInfosType} 各类域名
 * @author yuiffydong
 */
export function getDomains(host) {
  // QCMAIN_HOST 当前域名，如www.tencentcloud.com/intl.cloud.tencent.com/dev.intl.cloud.tencent.com:3001
  const QCMAIN_HOST = host || BASE_DOMAINS.QCMAIN_HOST;
  // QCMAIN_ROOT_HOST 国际站根域名，用于拼接其他域名，不导出。去除首部dev/www和尾部端口号，以便本地调试也能正常使用。如tencentcloud.com/intl.cloud.tencent.com
  const QCMAIN_ROOT_HOST = QCMAIN_HOST.replace(/^(dev\.)?(www\.)?/, '').replace(/:\d+$/, '');
  const QCLOUD_ROOT_HOST = QCMAIN_ROOT_HOST.replace(/^intl\./, ''); // 根域名，用于cookie。如cloud.tencent.com/tencentcloud.com
  const { CHINA_HOST } = BASE_DOMAINS; // 中国站域名，用于跳转
  const QCCONSOLE_HOST = `console.${QCMAIN_ROOT_HOST}`; // 国际控制台域名。新域名是console.tencentcloud.com，旧域名的控制台叫console.intl.cloud.tencent.com
  const QCCONSOLE_CN_HOST = `console.${CHINA_HOST}`; // 中国站控制台域名，用于一些域名替换
  const QCBUY_HOST = `buy.${QCMAIN_ROOT_HOST}`; // 国际购买页域名
  const QCBUY_CN_HOST = `buy.${CHINA_HOST}`; // 中国站购买页域名

  return { QCMAIN_HOST, QCLOUD_ROOT_HOST, QCCONSOLE_HOST, QCCONSOLE_CN_HOST, QCBUY_HOST, QCBUY_CN_HOST, CHINA_HOST };
}

/**
 * 获取各种相对路径域名
 * @returns {DomainInfosType} 各类域名
 * @author yuiffydong
 */
export const getDomainInfos = () => {
  let domainInfos = {};
  // console.log('getDomainInfos will judge is browser or not', { proBro: process.browser });
  if (typeof window === 'undefined') {
    // domainInfos = _.get(process, 'domain.req.res.$domains') || {}; // 旧官网是用中间件保存在这，直接获取来用
    // TODO: 后端状态下获取域名
    domainInfos = getDomains(); // 获取默认域名。大仓此处暂时无法获取到当前域名。store.getDomains()会串请求
    // console.log('getDomainInfos on server-side', {
    //   domainInfos,
    // });
  } else if (process && process.browser === true) {
    // domainInfos = window.DOMAIN_LIST || {}; // 旧官网是用中间件保存在这，直接获取来用
    try {
      domainInfos = getDomains(window && window.location.host); // 前端下直接获取当前域名
    } catch (e) {
      logger.info('getDomainInfos get domain from window catch error', e);
    }
  } else {
    logger.info('useDomains can not get domain');
  }
  // console.log('domainInfos', { domainInfos, isClientContext, isServerContext });
  return domainInfos;
};

const parseUrl = (srcUrl) => {
  let protocal;
  let host;
  let path;

  if (srcUrl && /^((?:https?:)?\/\/)([^/]+)(\/.*)?$/.test(srcUrl.trim())) {
    protocal = RegExp.$1;
    host = RegExp.$2;
    path = RegExp.$3;
  } else {
    protocal = '';
    host = '';
    path = srcUrl;
  }

  const ret = {
    protocal,
    host,
    path,
  };

  // console.log('parseUrl', srcUrl, ret);

  return ret;
};

/**
 * 生成域名替换用的map，从key替换到value
 * @param {{domainInfos?: DomainInfosType, extendReplaceOn?: boolean}} options
 * domainInfos为传入域名，不传入会自动获取，extendReplaceOn为是否开启额外替换
 */
const makeReplaceMap = (options = {}) => {
  const domainInfos = _.get(options, 'domainInfos') || getDomainInfos();
  // 第一阶段：额外处理，如把中国站控制台链接console.cloud.tencent.com处理为国际站控制台链接console.intl.cloud.tencent.com
  let replaceMapStep1 = {};
  const extendMapOn = _.has(options, 'extendReplaceOn')
    ? options.extendReplaceOn
    : _.get(commonConfig, 'domainFormatConfig.defaultExtendReplaceOn') || false;
  if (extendMapOn) {
    replaceMapStep1 = _.get(commonConfig, 'domainFormatConfig.extendReplaceMap') || {};
  }

  // 第二阶段：把同等的数个域名转换为BASE_DOMAINS中的格式，方便后续根据将其根据BASE_DOMAINS替换为当前域名
  const replaceMapStep2 = {};
  const sameDomainList = _.get(commonConfig, 'domainFormatConfig.sameDomainList') || [];
  sameDomainList.forEach((domainArr) => {
    const baseDomain = domainArr && domainArr[0];
    if (baseDomain) domainArr.slice(1).forEach((oneDomain) => {
      replaceMapStep2[oneDomain] = baseDomain;
    });
  });

  // 第三阶段：主要阶段，把BASE_DOMAINS里的域名替换为当前域名的对应形式
  const replaceMapStep3 = {};
  Object.keys(BASE_DOMAINS).forEach((domainKey) => {
    if (domainInfos[domainKey]) replaceMapStep3[BASE_DOMAINS[domainKey]] = domainInfos[domainKey];
  });
  const result = relationMapCombination([replaceMapStep1, replaceMapStep2, replaceMapStep3]);
  // console.log('makeReplaceMap', { replaceMapStep1, replaceMapStep2, replaceMapStep3, result });
  return result;
};

/**
 * 设置。可以通过domainInfos设置指定各个域名，而不是用自动获取的域名。
 * @typedef {Object} HostRelativeOptionsType
 * @property {DomainInfosType?} domainInfos
 */

/**
 * 把基础的.com的host转换为根据当前域名的相对域名(.com.cn/.cn）
 * @param {String} host 基本域名，如intl.console.cloud.tencent.com
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @returns {String} 相对的域名，如intl.console.cloud.tencent.cn
 */
const hostRelative = (host, options = {}) => {
  const replaceMap = makeReplaceMap(options);
  const multiReplacer = new MultiReplacer(replaceMap);
  return multiReplacer.replaceExact(host);
};

/**
 * 将url转化为符合相应域名（比如当前域名是intl.cloud.tencent.cn，就要把console.intl.cloud.tencent.com转化为console.intl.cloud.tencent.cn）的url。
 * 用于后台数据中获取的链接。
 * @param  {String} srcUrl 需要被转换的url
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @return {String} 转换后的url
 * @author yuiffydong
 */
export const formatUrlDomain = (srcUrl, options) => {
  let { protocal, host, path } = parseUrl(srcUrl);
  const keepUnnecessaryDomainInUrl = _.get(commonConfig, 'domainFormatConfig.keepUnnecessaryDomainInUrl') || false;
  if (!keepUnnecessaryDomainInUrl && host === BASE_DOMAINS.QCMAIN_HOST) {
    // 如果是官网域名，则用相对路径，不带域名
    protocal = '';
    host = '';
    if (!path) path = '/'; // 如果只有域名没有path，则path写个斜杠，以便返回一个"/"相对路径的首页
  } else if (host) {
    // 非官网域名，则进行域名转换
    host = hostRelative(host, options);
  }
  const ret = `${protocal}${host}${path}` || srcUrl;
  // console.log('formatUrlDomain', srcUrl, {protocal, host, path, ret});
  return ret;
};

export const isForeignLink = (redirectUrl) => {
  if (!redirectUrl) return false;
  const { host } = parseUrl(redirectUrl);
  return !Object.values(BASE_DOMAINS).includes(host);
};

/**
 * 对整篇文本中的链接的域名进行处理，把基础的.com的host转换为根据当前域名的相对域名(.com.cn/.cn）
 * @param {String} host 原文本
 * @param {HostRelativeOptionsType} options 设置{@link HostRelativeOptionsType}
 * @returns {String} 处理后文本
 */
export const hostRelativeReplaceAll = (text, options = {}) => {
  const replaceMapBase = makeReplaceMap(options);
  const replaceMap = {};
  Object.keys(replaceMapBase).forEach((replaceKey) => {
    // 增加双斜杠，使其只替换带有双斜杠的网址，尽量避免替换到非链接的地方
    if (replaceMapBase[replaceKey]) replaceMap[`//${replaceKey}`] = `//${replaceMapBase[replaceKey]}`;
  });
  const multiReplacer = new MultiReplacer(replaceMap);
  return multiReplacer.replace(text);
};

/**
 * 判断是否允许某域名下的跨域接口访问。比如控制台console.tencentcloud.com会调用官网的接口。
 * @param {String} domain 来源域名
 * @returns {boolean} 是否允许
 */
export const accessControlDomainTest = (domain) => /(cloud\.tencent\.com)|(cloud\.tencent\.cn)|(cloud\.tencent\.com\.cn)|(tencentcloud.com)$/.test(domain);
